import React, { useEffect, useState, useRef } from 'react';
import Datatable from '../../../components/Datatable';
import FormDialog from '../../../components/Datatable/FormDialog';
import { Box, Button, Checkbox, Chip, FormControlLabel, FormGroup, IconButton, Tooltip, Typography, CircularProgress, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Table, TableHead, TableBody, TableRow, TableCell, Switch } from '@mui/material';
import api, { create, get_one, update, delete_one } from '../../../services/api';
import EditIcon from '@mui/icons-material/Edit';
import { useNotify } from '../../../components/Datatable/NotifyProvider';
import { useFormError } from '../../../components/Datatable/FormErrorProvider';
import { PlayArrow, RemoveCircleOutline, Visibility, HourglassEmpty, Sync, CheckCircle, Error, Schedule, ConfirmationNumber, RocketLaunch, TaskAlt, SupportAgent, History } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../../components/AuthAdminProvider';
import CustomTextField from '../../../components/CustomTextField';
import HermesTypeSelect from '../../../components/HermesTypeSelect';
import TunnelSelect from '../../../components/TunnelSelect';
import EmpresaSelect from '../../../components/EmpresaSelect';
import BtnZabbix from '../../hermes/Zabbix';
import { BackupDialog } from '../../../components/BackupView';
import HostFilterBar, { initialHostFilters, toDatatableFilters } from '../../../components/HostFilterBar';
import BulkHostDialog from '../../../components/BulkHostDialog';
import useSelecaoPorId from '../../../hooks/useSelecaoPorId';
import SelecaoHostsBar from '../../../components/SelecaoHostsBar';
import SelecaoHostsDialog from '../../../components/SelecaoHostsDialog';
import ExecucoesHostDialog from '../../../components/ExecucoesHostDialog';
import TuneIcon from '@mui/icons-material/Tune';
import useSSE from '../../../hooks/useSSE';
import moment from 'moment';

const getItem = (storage, key) => {
    const data = localStorage.getItem(storage)
    return data ? Object.keys(JSON.parse(data)).includes(key) ? JSON.parse(data)[key] : null : null
}

const ATHENA_TICKET_URL = 'https://athena.ipv7.com.br/ticket'

export default function Host() {
    const notify = useNotify()
    const auth = useAuth()
    const [formState, setFormState] = useState({ open: false, id: null })
    const [hostFilters, setHostFilters] = useState(() => initialHostFilters(true))
    const { empresa, typeFilter } = hostFilters
    const [types, setTypes] = useState([])
    const error = useFormError()
    const [refresh, setRefresh] = useState(false)
    const [backupStatus, setBackupStatus] = useState({})
    const backupStatusRef = useRef({})
    const [backupDialog, setBackupDialog] = useState({ open: false, id: null })
    const [loadingBackup, setLoadingBackup] = useState({})
    const [loadingAction, setLoadingAction] = useState({})
    const [loadingBulk, setLoadingBulk] = useState(false)
    const [loadingSync, setLoadingSync] = useState(false)
    const [bulkDialog, setBulkDialog] = useState(false)
    const [bulkCredDialog, setBulkCredDialog] = useState(false)
    const [loadingBulkAction, setLoadingBulkAction] = useState(false)
    const [ticketDialog, setTicketDialog] = useState(false)
    const [execDialog, setExecDialog] = useState({ open: false, hostId: null, hostName: '' })
    const [activeHostIds, setActiveHostIds] = useState([])
    const [tableData, setTableData] = useState([])

    // Seleção por id, não por índice de linha — ver hooks/useSelecaoPorId.
    const selecao = useSelecaoPorId(tableData)
    const [verSelecao, setVerSelecao] = useState(false)

    // SSE: connect when there are active (queued/running) hosts
    const sseHostIds = activeHostIds.join(',')
    const { data: sseEvent } = useSSE(
        '/admin/hermes/backup/stream',
        { host_ids: sseHostIds },
        sseHostIds.length > 0
    )

    // Process SSE events
    useEffect(() => {
        if (!sseEvent || !sseEvent.host_id) return

        const hostId = sseEvent.host_id
        const progress = sseEvent.progress || sseEvent

        setBackupStatus(prev => {
            const newState = { ...prev, [hostId]: { ...prev[hostId], ...sseEvent, progress } }
            backupStatusRef.current = newState
            return newState
        })

        // If done or error, remove from active list and show notification
        const step = progress?.step || sseEvent?.step
        if (step === 'done') {
            setActiveHostIds(prev => prev.filter(id => id !== hostId))
            setBackupStatus(prev => ({
                ...prev,
                [hostId]: { ...prev[hostId], completedAt: Date.now(), completedStatus: 'success', status: 'idle' }
            }))
            notify.setNotify({ open: true, message: 'Backup concluído com sucesso!', severity: 'success' })
            // Clear highlight after 30s
            setTimeout(() => {
                setBackupStatus(prev => {
                    const curr = prev[hostId]
                    if (curr?.completedAt && Date.now() - curr.completedAt >= 29000) {
                        return { ...prev, [hostId]: { ...curr, completedAt: null, completedStatus: null } }
                    }
                    return prev
                })
            }, 30000)
        } else if (step === 'error') {
            setActiveHostIds(prev => prev.filter(id => id !== hostId))
            setBackupStatus(prev => ({
                ...prev,
                [hostId]: { ...prev[hostId], completedAt: Date.now(), completedStatus: 'error', errorMessage: progress?.message || sseEvent?.message, status: 'idle' }
            }))
            notify.setNotify({ open: true, message: progress?.message || 'Erro no backup', severity: 'error' })
            setTimeout(() => {
                setBackupStatus(prev => {
                    const curr = prev[hostId]
                    if (curr?.completedAt && Date.now() - curr.completedAt >= 29000) {
                        return { ...prev, [hostId]: { ...curr, completedAt: null, completedStatus: null } }
                    }
                    return prev
                })
            }, 30000)
        }
    }, [sseEvent])

    useEffect(() => {
        document.title = "IPV7 Hermes - Host"
        error.setErrors([])
    }, [])

    // Process hosts loaded from datatable — detect queued/running for SSE
    const handleDataLoaded = (hosts) => {
        if (!hosts || hosts.length === 0) { setTableData([]); return }
        setTableData(hosts)

        const newActiveIds = []
        setBackupStatus(prev => {
            const newStatuses = { ...prev }
            hosts.forEach(host => {
                const currentStatus = backupStatusRef.current[host.id]
                const backendStatus = host.queue_status || 'idle'

                // Don't overwrite if we have active SSE data
                if (currentStatus?.status === 'running' || currentStatus?.status === 'queued') {
                    if (backendStatus === 'queued' || backendStatus === 'running') {
                        newActiveIds.push(host.id)
                    }
                    return
                }

                newStatuses[host.id] = {
                    status: backendStatus,
                    position: host.queue_position,
                    total_queue: host.queue_total,
                    progress: host.queue_progress,
                    has_ticket: host.has_ticket,
                    ticket_id: host.ticket_id,
                }

                if (backendStatus === 'queued' || backendStatus === 'running') {
                    newActiveIds.push(host.id)
                }
            })
            backupStatusRef.current = newStatuses
            return newStatuses
        })

        setActiveHostIds(prev => {
            const merged = new Set([...prev, ...newActiveIds])
            return [...merged]
        })
    }

    const runBackup = async (hostId) => {
        setLoadingAction(prev => ({ ...prev, [hostId]: true }))
        try {
            const res = await api.get(`/admin/hermes/backup/run/${hostId}`)
            if (res.data.error) {
                notify.setNotify({ open: true, message: res.data.error, severity: 'error' })
                return
            }
            if (res.data.warning) {
                notify.setNotify({ open: true, message: res.data.warning, severity: 'warning' })
                return
            }
            setBackupStatus(prev => {
                const newState = { ...prev, [hostId]: { status: 'queued', message: 'Adicionado na fila', position: 1 } }
                backupStatusRef.current = newState
                return newState
            })
            setActiveHostIds(prev => [...new Set([...prev, hostId])])
        } catch (e) {
            notify.setNotify({ open: true, message: "Erro ao solicitar backup", severity: 'error' })
        } finally {
            setLoadingAction(prev => ({ ...prev, [hostId]: false }))
        }
    }

    const cancelBackup = async (hostId) => {
        setLoadingAction(prev => ({ ...prev, [hostId]: true }))
        try {
            const res = await api.delete(`/admin/hermes/backup/cancel/${hostId}`)
            if (res.data.error) {
                notify.setNotify({ open: true, message: res.data.error, severity: 'error' })
                return
            }
            notify.setNotify({ open: true, message: "Backup cancelado", severity: 'success' })
            setActiveHostIds(prev => prev.filter(id => id !== hostId))
            setBackupStatus(prev => ({ ...prev, [hostId]: { ...prev[hostId], status: 'idle' } }))
        } catch (e) {
            notify.setNotify({ open: true, message: "Erro ao cancelar backup", severity: 'error' })
        } finally {
            setLoadingAction(prev => ({ ...prev, [hostId]: false }))
        }
    }

    const runBulkBackup = async (onlyErrors = false) => {
        setBulkDialog(false)
        if (!empresa) {
            notify.setNotify({ open: true, message: "Selecione uma empresa primeiro", severity: 'warning' })
            return
        }
        setLoadingBulk(true)
        try {
            const body = { empresa_id: empresa, only_errors: onlyErrors }
            if (typeFilter) body.type_filter = types.find(t => t.id === typeFilter)?.type
            const res = await api.post('/admin/hermes/backup/run-bulk', body)
            const { enqueued, skipped, total } = res.data
            notify.setNotify({
                open: true,
                message: `${enqueued} hosts enfileirados (${skipped} já na fila, ${total} total)`,
                severity: enqueued > 0 ? 'success' : 'info'
            })
            setTimeout(() => setRefresh(prev => !prev), 1000)
        } catch (e) {
            notify.setNotify({ open: true, message: "Erro ao enfileirar backups", severity: 'error' })
        } finally {
            setLoadingBulk(false)
        }
    }

    // Bulk operations on selected hosts
    const getSelectedHostIds = () => selecao.ids

    const bulkRunSelected = async () => {
        const ids = getSelectedHostIds()
        if (ids.length === 0) return
        setLoadingBulkAction(true)
        let enqueued = 0
        for (const id of ids) {
            try {
                const res = await api.get(`/admin/hermes/backup/run/${id}`)
                if (!res.data.error && !res.data.warning) enqueued++
            } catch (e) { }
        }
        notify.setNotify({ open: true, message: `${enqueued} backups enfileirados`, severity: 'success' })
        selecao.limpar()
        setLoadingBulkAction(false)
        setTimeout(() => setRefresh(prev => !prev), 1000)
    }

    const resolveError = async (hostId) => {
        try {
            await api.post(`/admin/hermes/backup/resolve/${hostId}`)
            notify.setNotify({ open: true, message: 'Erro marcado como resolvido', severity: 'success' })
            setBackupStatus(prev => ({
                ...prev, [hostId]: { ...prev[hostId], completedStatus: null, errorMessage: null, has_ticket: false, ticket_id: null }
            }))
            setRefresh(prev => !prev)
        } catch (e) {
            notify.setNotify({ open: true, message: 'Erro ao resolver', severity: 'error' })
        }
    }

    const openTicket = async (hostId) => {
        try {
            const res = await api.post(`/admin/hermes/backup/open-ticket/${hostId}`)
            if (res.data.error) {
                notify.setNotify({ open: true, message: res.data.error, severity: 'warning' })
                return
            }
            notify.setNotify({ open: true, message: res.data.message || 'Chamado aberto', severity: 'success' })
            const raw = res.data.ticket
            const ticketId = raw && /^\d+$/.test(String(raw)) ? String(raw) : null
            setBackupStatus(prev => ({ ...prev, [hostId]: { ...prev[hostId], has_ticket: true, ticket_id: ticketId } }))
        } catch (e) {
            notify.setNotify({ open: true, message: 'Erro ao abrir chamado', severity: 'error' })
        }
    }

    const bulkResolve = async () => {
        const ids = getSelectedHostIds()
        if (ids.length === 0) return
        setLoadingBulkAction(true)
        let count = 0
        for (const id of ids) {
            try { await api.post(`/admin/hermes/backup/resolve/${id}`); count++ } catch (e) { }
        }
        notify.setNotify({ open: true, message: `${count} erro(s) resolvidos`, severity: 'success' })
        selecao.limpar()
        setLoadingBulkAction(false)
        setRefresh(prev => !prev)
    }

    const bulkOpenTickets = async (aggregated = false) => {
        setTicketDialog(false)
        const ids = getSelectedHostIds()
        if (ids.length === 0) return
        setLoadingBulkAction(true)

        if (aggregated) {
            // One ticket for all selected hosts
            try {
                const res = await api.post('/admin/hermes/backup/open-ticket-bulk', { host_ids: ids })
                if (res.data.error) {
                    notify.setNotify({ open: true, message: res.data.error, severity: 'warning' })
                } else {
                    notify.setNotify({ open: true, message: res.data.message || 'Chamado agregado aberto', severity: 'success' })
                }
            } catch (e) {
                notify.setNotify({ open: true, message: 'Erro ao abrir chamado', severity: 'error' })
            }
        } else {
            // One ticket per host
            let count = 0
            for (const id of ids) {
                try {
                    const res = await api.post(`/admin/hermes/backup/open-ticket/${id}`)
                    if (!res.data.error) count++
                } catch (e) { }
            }
            notify.setNotify({ open: true, message: `${count} chamado(s) abertos`, severity: 'success' })
        }

        selecao.limpar()
        setLoadingBulkAction(false)
        setRefresh(prev => !prev)
    }

    const syncHorus = async () => {
        setLoadingSync(true)
        try {
            const params = empresa ? { empresa } : {}
            const res = await api.get('/admin/hermes/backup/sync/horus', { params })
            const d = res.data || {}
            if (d.error) {
                notify.setNotify({ open: true, message: d.error, severity: 'error' })
            } else {
                const created = d.created || 0
                const existing = d.existing || 0
                const skipped = d.skipped || 0
                const noVendor = d.no_vendor || 0
                const parts = [`${created} adicionados`]
                if (existing > 0) parts.push(`${existing} existentes`)
                if (skipped > 0) parts.push(`${skipped} empresas sem config`)
                if (noVendor > 0) parts.push(`${noVendor} sem vendor`)
                notify.setNotify({
                    open: true,
                    message: `Sync Horus: ${parts.join(', ')}`,
                    severity: created > 0 ? 'success' : 'info'
                })
                if (created > 0) setRefresh(prev => !prev)
            }
        } catch (e) {
            const msg = e.response?.data?.detail || e.response?.data?.error || 'Erro no sync Horus'
            notify.setNotify({ open: true, message: msg, severity: 'error' })
        } finally {
            setLoadingSync(false)
        }
    }

    // Quem busca as execuções agora é o ExecucoesHostDialog.
    const openExecutions = (hostId, hostName) =>
        setExecDialog({ open: true, hostId, hostName })

    const openBackups = async (hostId) => {
        setLoadingBackup(prev => ({ ...prev, [hostId]: true }))
        try {
            const res = await api.get(`/admin/hermes/backup/host/${hostId}`)
            if (res.data.error) {
                notify.setNotify({ open: true, message: res.data.error, severity: 'info' })
            } else if (res.data.backup_id) {
                setBackupDialog({ open: true, id: res.data.backup_id })
            }
        } catch (e) {
            notify.setNotify({ open: true, message: "Erro ao buscar backups", severity: 'error' })
        } finally {
            setLoadingBackup(prev => ({ ...prev, [hostId]: false }))
        }
    }

    // Backup status indicator — error message shown inline below status
    const BackupStatusIndicator = ({ rowData }) => {
        const hostId = rowData[0]
        const statusData = backupStatus[hostId] || {}

        // Indices: 0=id, 1=empresa_name, 2=host_name, 3=ip, 4=type_name, 5=vendor_name, 6=status
        // 7=last_backup_date, 8=last_log_status, 9=last_log_message, 10=last_log_date, 11=backup_count
        // 12=backup_info (virtual), 13=actions (virtual), 14=has_ticket, 15=ticket_id
        const lastBackupDate = rowData[7]
        const lastLogStatus = rowData[8]
        const lastLogDate = rowData[10]
        const backupCount = rowData[11] || 0
        const hasTicket = statusData.has_ticket || rowData[14]
        const ticketId = statusData.ticket_id || rowData[15]

        const lastLogMessage = statusData.errorMessage || rowData[9]

        const hasDynamicStatus = statusData.completedStatus || statusData.errorMessage
        const hasError = hasDynamicStatus
            ? (statusData.completedStatus === 'error' || !!statusData.errorMessage)
            : lastLogStatus === 0
        const hasSuccess = hasDynamicStatus
            ? statusData.completedStatus === 'success'
            : lastLogStatus === 1

        const isRecent = statusData.completedAt && (Date.now() - statusData.completedAt < 30000)

        return (
            <Box>
                {/* First row: icon + count + date + ticket */}
                <Box display="flex" alignItems="center" gap={0.5}>
                    {hasError ? (
                        <Error fontSize="small" color="error" />
                    ) : hasSuccess ? (
                        <CheckCircle fontSize="small" color="success" />
                    ) : (
                        <Schedule fontSize="small" color="disabled" />
                    )}
                    {isRecent && (
                        <Chip size="small" label="Agora" color={hasError ? "error" : "success"}
                            sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }} />
                    )}
                    <Chip size="small" label={backupCount} variant="outlined"
                        sx={{ minWidth: 28, fontSize: '0.7rem' }} />
                    {lastBackupDate && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {moment(lastBackupDate).format('DD/MM HH:mm')}
                        </Typography>
                    )}
                    {hasTicket && (
                        <Tooltip title={ticketId ? `Ticket #${ticketId}` : 'Ticket aberto'}>
                            <IconButton size="small" sx={{ p: 0.2 }}
                                onClick={() => {
                                    if (ticketId && ticketId !== 'pending' && ticketId !== 'sent') {
                                        window.open(`${ATHENA_TICKET_URL}/${ticketId}`, '_blank')
                                    }
                                }}>
                                <ConfirmationNumber fontSize="small" sx={{ color: '#ff9800', fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                {/* Second row: error message inline */}
                {hasError && lastLogMessage && (
                    <Typography variant="caption" color="error" sx={{
                        fontSize: '0.65rem', display: 'block', mt: 0.3,
                        maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {lastLogMessage}
                    </Typography>
                )}
            </Box>
        )
    }

    // Backup action button with real-time progress
    const BackupButton = ({ hostId }) => {
        const statusData = backupStatus[hostId] || {}
        const status = statusData.status || 'idle'
        const progress = statusData.progress || {}
        const position = statusData.position
        const totalQueue = statusData.total_queue
        const isLoading = loadingAction[hostId]

        if (isLoading) {
            return <IconButton size="small" disabled><CircularProgress size={18} /></IconButton>
        }

        if (status === 'running') {
            const progressPercent = progress.percent || 0
            const step = progress.step || 'running'
            const stepLabels = {
                'connecting': 'Conectando', 'auth': 'Autenticando',
                'commands': 'Executando', 'saving': 'Salvando', 'running': 'Executando'
            }
            return (
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Chip
                        size="small"
                        icon={<Sync sx={{
                            fontSize: 14,
                            animation: 'spin 1.5s linear infinite',
                            '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } }
                        }} />}
                        label={stepLabels[step] || 'Executando'}
                        color="warning" variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 24, '& .MuiChip-label': { px: 0.5 } }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {progressPercent}%
                    </Typography>
                </Box>
            )
        }

        if (status === 'queued') {
            const queueInfo = position && totalQueue ? `Posição ${position} de ${totalQueue}` : 'Na fila'
            return (
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title={queueInfo}>
                        <HourglassEmpty sx={{ color: 'info.main', fontSize: 20 }} />
                    </Tooltip>
                    {position && (
                        <Chip size="small" label={`#${position}`} color="info" variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 18 }} />
                    )}
                </Box>
            )
        }

        if (progress.step === 'done') {
            return (
                <Tooltip title="Backup concluído recentemente">
                    <IconButton size="small" onClick={() => runBackup(hostId)} sx={{ color: 'success.main' }}>
                        <CheckCircle />
                    </IconButton>
                </Tooltip>
            )
        }

        if (progress.step === 'error') {
            return (
                <Tooltip title={progress.message || "Erro no último backup"}>
                    <IconButton size="small" disabled={!auth.hasPerm('backup.create')} onClick={() => runBackup(hostId)} sx={{ color: 'error.main' }}>
                        <Error />
                    </IconButton>
                </Tooltip>
            )
        }

        return (
            <Tooltip title="Executar backup">
                <IconButton size="small" disabled={!auth.hasPerm('backup.create')} onClick={() => runBackup(hostId)}>
                    <PlayArrow />
                </IconButton>
            </Tooltip>
        )
    }

    return (
        <>
            <BackupDialog
                admin
                open={backupDialog.open}
                id={backupDialog.id}
                handlerClose={() => setBackupDialog({ open: false, id: null })}
            />
            <FormItem
                open={formState.open}
                id={formState.id}
                handlerClose={() => setFormState({ open: false, id: null })}
                handlerSubmit={() => {
                    setFormState({ open: false, id: null })
                    setRefresh(true)
                }}
                btnSubmit={formState.id ? 'Atualizar' : 'Salvar'}
                btnClose={'Fechar'}
            />

            {/* Barra de filtros compartilhada com a tela do cliente. */}
            <HostFilterBar
                admin
                value={hostFilters}
                onChange={setHostFilters}
                onTypesLoaded={setTypes}
            >
                <Tooltip title="Rodar backup em massa da empresa selecionada">
                    <span>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={loadingBulk ? <CircularProgress size={16} /> : <RocketLaunch />}
                            disabled={!empresa || loadingBulk || !auth.hasPerm('backup.create')}
                            onClick={() => setBulkDialog(true)}
                            sx={{ textTransform: 'none' }}
                        >
                            Backup em Massa
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title="Sincronizar hosts do Horus/Zabbix">
                    <span>
                        <Button
                            variant="outlined"
                            size="small"
                            color="info"
                            startIcon={loadingSync ? <CircularProgress size={16} /> : <Sync />}
                            disabled={loadingSync || !auth.hasPerm('host.create')}
                            onClick={syncHorus}
                            sx={{ textTransform: 'none' }}
                        >
                            Sync Horus
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title="Adicionar host">
                    <IconButton disabled={!auth.hasPerm('host.create')}
                        onClick={() => setFormState({ open: true, id: null })}
                    >
                        <AddIcon />
                    </IconButton>
                </Tooltip>
                <BtnZabbix callback={() => setRefresh(true)} admin empresa={empresa} />
            </HostFilterBar>

            {/* Bulk backup dialog */}
            <Dialog open={bulkDialog} onClose={() => setBulkDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Backup em Massa</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Escolha quais hosts da empresa serão enfileirados para backup:
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setBulkDialog(false)} color="inherit">Cancelar</Button>
                    <Button onClick={() => runBulkBackup(true)} variant="outlined" color="warning">
                        Só com Erros
                    </Button>
                    <Button onClick={() => runBulkBackup(false)} variant="contained">
                        Todos os Hosts
                    </Button>
                </DialogActions>
            </Dialog>

            <BulkHostDialog
                admin
                open={bulkCredDialog}
                onClose={() => setBulkCredDialog(false)}
                hostIds={getSelectedHostIds()}
                empresa={empresa}
                onDone={({ severity, message }) => {
                    notify.setNotify({ open: true, message, severity })
                    selecao.limpar()
                    setRefresh(prev => !prev)
                }}
            />

            <ExecucoesHostDialog
                admin
                open={execDialog.open}
                hostId={execDialog.hostId}
                hostName={execDialog.hostName}
                onClose={() => setExecDialog({ open: false, hostId: null, hostName: '' })}
            />

            {/* Bulk ticket dialog */}
            <Dialog open={ticketDialog} onClose={() => setTicketDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Abrir Chamados</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {selecao.ids.length} host(s) selecionados. Como deseja abrir os chamados?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setTicketDialog(false)} color="inherit">Cancelar</Button>
                    <Button onClick={() => bulkOpenTickets(true)} variant="outlined">
                        Um Chamado Agregado
                    </Button>
                    <Button onClick={() => bulkOpenTickets(false)} variant="contained">
                        Um por Host
                    </Button>
                </DialogActions>
            </Dialog>

            <SelecaoHostsDialog
                open={verSelecao}
                onClose={() => setVerSelecao(false)}
                itens={selecao.itens}
                onRemover={selecao.remover}
                onLimpar={() => { selecao.limpar(); setVerSelecao(false) }}
            >
                <Button size="small" variant="outlined" startIcon={<PlayArrow />}
                    disabled={loadingBulkAction}
                    onClick={() => { setVerSelecao(false); bulkRunSelected() }}
                    sx={{ textTransform: 'none' }}>
                    Executar Backup
                </Button>
                <Button size="small" variant="contained" color="warning" startIcon={<TuneIcon />}
                    disabled={loadingBulkAction || !auth.hasPerm('host.update')}
                    onClick={() => { setVerSelecao(false); setBulkCredDialog(true) }}
                    sx={{ textTransform: 'none' }}>
                    Alteração em Massa
                </Button>
            </SelecaoHostsDialog>

            <SelecaoHostsBar
                total={selecao.ids.length}
                nestaPagina={selecao.rowsSelected.length}
                onLimpar={selecao.limpar}
                onVerSelecao={() => setVerSelecao(true)}
            >
                    <Button size="small" variant="outlined" startIcon={<PlayArrow />}
                    disabled={loadingBulkAction}
                    onClick={bulkRunSelected}
                    sx={{ textTransform: 'none' }}>
                    Executar Backup
                </Button>
                <Button size="small" variant="outlined" color="warning" startIcon={<TuneIcon />}
                    disabled={loadingBulkAction || !auth.hasPerm('host.update')}
                    onClick={() => setBulkCredDialog(true)}
                    sx={{ textTransform: 'none' }}>
                    Alteração em Massa
                </Button>
                <Button size="small" variant="outlined" color="success"
                    disabled={loadingBulkAction}
                    onClick={bulkResolve}
                    sx={{ textTransform: 'none' }}>
                    Resolver Erros
                </Button>
                <Button size="small" variant="outlined"
                    disabled={loadingBulkAction}
                    onClick={() => setTicketDialog(true)}
                    sx={{ textTransform: 'none' }}>
                    Abrir Chamados
                </Button>
                {loadingBulkAction && <CircularProgress size={18} />}
            </SelecaoHostsBar>

            <Datatable
                empresa={empresa}
                filters={toDatatableFilters(hostFilters)}
                refresh={refresh}
                handlerRefresh={() => setRefresh(!refresh)}
                title='Hosts'
                endpoint='/admin/hermes/host'
                onDataLoaded={handleDataLoaded}
                options={{
                    selectableRows: 'multiple',
                    rowsSelected: selecao.rowsSelected,
                    onRowSelectionChange: selecao.onRowSelectionChange,
                    // A barra de seleção vive fora da tabela (ver SelecaoHostsBar).
                    // `customToolbarSelect` vazio ainda deixaria a barra nativa
                    // desenhada com "N linha(s) selecionada(s)" — duplicando o
                    // contador. `none` remove a barra inteira.
                    selectToolbarPlacement: 'none',
                    setRowProps: (row) => {
                        const hostId = row[0]
                        const statusData = backupStatus[hostId] || {}
                        const isInactive = row[6] === 0 || row[6] === false
                        const lastLogStatus = row[8]
                        const hasError = statusData.completedStatus === 'error' || statusData.errorMessage || lastLogStatus === 0

                        const isRecentSuccess = statusData.completedAt && statusData.completedStatus === 'success'
                        const isRecentError = statusData.completedAt && statusData.completedStatus === 'error'

                        let style = {}
                        if (isRecentSuccess) {
                            style = { backgroundColor: '#e8f5e9', transition: 'background-color 1s ease' }
                        } else if (isRecentError) {
                            style = { backgroundColor: '#ffebee', transition: 'background-color 1s ease' }
                        } else if (hasError) {
                            style = { backgroundColor: '#fff5f5' }
                        } else if (isInactive) {
                            style = { backgroundColor: '#f5f5f5', opacity: 0.7 }
                        }

                        return { style }
                    }
                }}
                columns={[
                    { name: 'id', label: 'ID', options: { display: false } },
                    {
                        name: 'empresa_name', label: 'Empresa / Host',
                        options: {
                            customBodyRender: (value, tableMeta) => {
                                const hostName = tableMeta.rowData[2]
                                const ip = tableMeta.rowData[3]
                                const typeName = tableMeta.rowData[4]
                                const vendor = tableMeta.rowData[5]
                                return (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{value}</Typography>
                                        <Typography variant="body2" fontWeight="medium">{hostName}</Typography>
                                        <Typography variant="caption" color="text.secondary">{ip}</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                                            {vendor} — {typeName}
                                        </Typography>
                                    </Box>
                                )
                            }
                        }
                    },
                    { name: 'host_name', label: 'Host', options: { display: false } },
                    { name: 'ip', label: 'IP', options: { display: false } },
                    { name: 'type_name', label: 'Tipo', options: { display: false } },
                    { name: 'vendor_name', label: 'Vendor', options: { display: false } },
                    { name: 'status', label: 'Status', options: { display: false } },
                    { name: 'last_backup_date', label: 'Último Backup', options: { display: false } },
                    { name: 'last_log_status', label: 'Log Status', options: { display: false } },
                    { name: 'last_log_message', label: 'Log Message', options: { display: false } },
                    { name: 'last_log_date', label: 'Log Date', options: { display: false } },
                    { name: 'backup_count', label: 'Qtd Backups', options: { display: false } },
                    {
                        name: 'backup_info', label: 'Backup',
                        options: {
                            filter: false, sort: false,
                            customBodyRender: (value, tableMeta) => <BackupStatusIndicator rowData={tableMeta.rowData} />
                        }
                    },
                    {
                        name: 'actions', label: 'Ações',
                        options: {
                            filter: false, sort: false,
                            customBodyRender: (value, tableMeta) => {
                                const hostId = tableMeta.rowData[0]
                                const hostName = tableMeta.rowData[2]
                                const isLoadingBackup = loadingBackup[hostId]
                                const statusData = backupStatus[hostId] || {}
                                const lastLogStatus = tableMeta.rowData[8]
                                const hostHasError = statusData.completedStatus === 'error' || statusData.errorMessage || lastLogStatus === 0
                                const hostHasTicket = statusData.has_ticket || tableMeta.rowData[14]
                                return (
                                    <Box display="flex" gap={0.5}>
                                        <Tooltip title="Histórico de execuções">
                                            <IconButton size="small" onClick={() => openExecutions(hostId, hostName)}>
                                                <History fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Ver backups">
                                            <IconButton size="small" onClick={() => openBackups(hostId)} disabled={isLoadingBackup}>
                                                {isLoadingBackup ? <CircularProgress size={18} /> : <Visibility />}
                                            </IconButton>
                                        </Tooltip>
                                        <BackupButton hostId={hostId} />
                                        {hostHasError && (
                                            <Tooltip title="Marcar como resolvido">
                                                <IconButton size="small" onClick={() => resolveError(hostId)} sx={{ color: 'success.main' }}>
                                                    <TaskAlt fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {hostHasError && !hostHasTicket && (
                                            <Tooltip title="Abrir chamado no Athena">
                                                <IconButton size="small" onClick={() => openTicket(hostId)} sx={{ color: 'warning.main' }}>
                                                    <SupportAgent fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Editar">
                                            <IconButton size="small" disabled={!auth.hasPerm('host.update')}
                                                onClick={() => setFormState({ open: true, id: hostId })}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )
                            }
                        }
                    },
                    { name: 'has_ticket', label: 'Ticket', options: { display: false } },
                    { name: 'ticket_id', label: 'Ticket ID', options: { display: false } },
                ]}
            />
        </>
    )
}


function FormItem({ id, open, handlerClose, handlerSubmit, btnClose = 'Fechar', btnSubmit = 'Salvar' }) {
    const notify = useNotify()
    const error = useFormError()
    const auth = useAuth()
    const [isLoaded, setIsLoaded] = React.useState(true)
    const modelState = {
        host_name: '',
        type_vendor: null,
        ip_address: '',
        username: '',
        password: '',
        port: '',
        status: 1,
        empresa: getItem('hermes', 'empresa') || null,
    }
    const [state, setState] = useState(modelState)

    useEffect(() => {
        setState(modelState)
        if (!open) return
        error.setErrors([])
        if (id) {
            setIsLoaded(false)
            get_one('/admin/hermes/host', id).then(res => {
                setState(res.data.data)
                setIsLoaded(true)
            }).catch(e => {
                console.log(e)
                setIsLoaded(true)
            })
        }
    }, [id, open])

    const handleChange = (event) => {
        setState({ ...state, [event.target.name]: event.target.value });
    }

    const wrapperSubmit = () => {
        if (id) {
            update('/admin/hermes/host', id, state).catch(e => notify
                .setNotify({ open: true, message: e.message, severity: 'error' }))
                .then(res => {
                    if (res.data.error) {
                        notify.setNotify({ open: true, message: res.data.error, severity: 'error' })
                        return
                    }
                    if (res.data.errors) {
                        error.setErrors(res.data.errors)
                        return
                    }
                    handlerSubmit()
                    notify.setNotify({ open: true, message: 'Host atualizado com sucesso', severity: 'success' })
                    error.setErrors([])
                }).catch(e => notify.setNotify({ open: true, message: e.message, severity: 'error' }))
        }
        else {
            create('/admin/hermes/host/create', state)
                .then(res => {
                    if (res.data.error) {
                        notify.setNotify({ open: true, message: res.data.error, severity: 'error' })
                        return
                    }
                    if (res.data.errors) {
                        error.setErrors(res.data.errors)
                        return
                    }
                    handlerSubmit()
                    notify.setNotify({ open: true, message: 'Host criado com sucesso.', severity: 'success' })
                    error.setErrors([])
                })
                .catch(e =>
                    notify.setNotify({ open: true, message: e.message, severity: 'error' }))
        }
    }

    return (
        <Box component={"form"} onSubmit={wrapperSubmit} sx={{ m: 2 }} display={'flex'} flexWrap={'wrap'}>
            <FormDialog
                isLoaded={isLoaded}
                title={
                    <Box display={"flex"} justifyContent={'space-between'}>
                        <Typography variant="h6" component="div">{id ? 'Editar' : 'Adicionar'} Host</Typography>
                        <IconButton disabled={!auth.hasPerm("host.delete")}
                            sx={{ display: id ? 'block' : 'none' }}
                            onClick={() => {
                                if (confirm('Deseja excluir o Host?')) {
                                    delete_one('/admin/hermes/host', id).then(() => {
                                        notify.setNotify({ open: true, message: 'Host deletado com sucesso.', severity: 'success' })
                                        handlerClose()
                                        handlerSubmit()
                                    })
                                }
                            }}>
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                }
                open={open}
                handlerSubmit={wrapperSubmit}
                handlerClose={handlerClose}
                btnSubmit={btnSubmit}
                btnClose={btnClose}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column' }} pt={1} gap={1}>
                    <EmpresaSelect
                        handler={(e) => setState({ ...state, empresa: e })} value={state.empresa}
                        disabled={id}
                    />
                    <CustomTextField error={error.verify('host_name')} helperText={error.msgError('host_name')}
                        label='Nome do Host' value={state?.host_name} name={'host_name'} onChange={handleChange} />
                    <HermesTypeSelect admin fullWidth handler={(type) => setState(prev => ({ ...prev, type }))}
                        value={state?.type} size="small" />
                    <CustomTextField error={error.verify('ip_address')} helperText={error.msgError('ip_address')}
                        label='IP' value={state?.ip_address} name={'ip_address'} onChange={handleChange} />
                    <CustomTextField error={error.verify('username')} helperText={error.msgError('username')}
                        label='Usuário' value={state?.username} name={'username'} onChange={handleChange} />
                    <CustomTextField error={error.verify('password')} helperText={error.msgError('password')}
                        label='Senha' value={state?.password} name={'password'} onChange={handleChange} />
                    <CustomTextField error={error.verify('enable_password')} helperText={error.msgError('enable_password')}
                        label='Enable Password' value={state?.enable_password} name={'enable_password'} onChange={handleChange} />
                    <CustomTextField error={error.verify('port')} helperText={error.msgError('port')}
                        label='Porta' value={state?.port} name={'port'} onChange={handleChange} />
                    <TunnelSelect empresa={state?.empresa} fullWidth admin={true}
                        handler={(tunnel) => setState(prev => ({ ...prev, interface: tunnel }))}
                        value={state?.interface} size="small" />
                    <FormGroup>
                        <FormControlLabel control={
                            <Checkbox checked={state?.status}
                                onChange={(e) => setState({ ...state, status: e.target.checked ? 1 : 0 })} />
                        } label="Ativo" />
                    </FormGroup>
                </Box>
            </FormDialog>
        </Box>
    )
}
